import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { CertAlertPreview, CertAlertRule, CertAlertRuleIn, CertAlertSentInfo, TlsRegister } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { usePermessiModuli } from '../auth/permessi'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { CRITICALITY_LABEL } from '../pages/Services'
import { Card, EmptyState, ErrorBanner, Spinner, StatCard, formatDateTime } from './common'
import { DataTable, type Column } from './DataTable'

/**
 * The «Notifiche» tab of Sicurezza → Cifratura: who is told, and when, about
 * the certificates that expire or are self-signed (cert_alert_service). A
 * summary, then two views — the rules (which certificates, which thresholds,
 * who receives them; an editor with a live preview of the next pass and a test
 * mail to oneself) and the log of the notices sent.
 */

const GIORNI_TIPICI = [90, 60, 30, 14, 7, 3, 1]

/** The repository types (pki_service.CERT_TYPES), in the order offered. */
const TIPI_REPOSITORY: Array<[string, () => string]> = [
  ['web', () => t('web')],
  ['ca', () => t('CA')],
  ['code_signing', () => t('firma del codice')],
  ['other', () => t('altro')],
  ['computer', () => t('computer')],
  ['user', () => t('utente')],
]

/** A history row about a repository certificate nobody uses (cert_key «pki:…»). */
export function nonUsato(chiave: string): boolean {
  return chiave.startsWith('pki:')
}

const NUOVA: Record<CertAlertRule['kind'], CertAlertRuleIn> = {
  scadenza: {
    name: '', kind: 'scadenza', enabled: true, days: [60, 30, 14, 7, 1], include_expired: true,
    repeat_expired_days: 7, notify_existing: false, only_exposed: true, match: [], services: [],
    repository_types: ['web', 'ca', 'code_signing', 'other'], min_criticality: '', recipients: [], include_tenant_recipients: false, include_service_owners: false,
  },
  autofirmato: {
    name: '', kind: 'autofirmato', enabled: true, days: [], include_expired: false, repeat_expired_days: 0,
    notify_existing: false, only_exposed: true, match: [], services: [], repository_types: [], min_criticality: '', recipients: [],
    include_tenant_recipients: false, include_service_owners: false,
  },
}

/** The codes of `cert_alert_service.ERRORS`, in words. */
function erroreRegola(codice: string): string {
  return ({
    no_recipients: t('Nessun destinatario: la regola non ha indirizzi e i destinatari generali sono vuoti.'),
    send_failed: t('Invio non riuscito: controlla la posta in uscita in Impostazioni.'),
    partial: t('Alcuni destinatari non hanno ricevuto l’avviso.'),
    register_unreadable: t('Il registro dei certificati non si è potuto leggere.'),
  } as Record<string, string>)[codice] ?? codice
}

function messaggio(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/** «60, 30 e 7 giorni prima · e da scaduto, ogni 7 giorni». */
export function quandoAvvisa(r: CertAlertRuleIn): string {
  if (r.kind === 'autofirmato') {
    return r.notify_existing ? t('quando ne compare uno (al primo controllo anche quelli già presenti)')
      : t('quando ne compare uno nuovo')
  }
  const parti: string[] = []
  if (r.days.length) parti.push(t('{giorni} giorni prima', { giorni: r.days.join(', ') }))
  if (r.include_expired) {
    parti.push(r.repeat_expired_days ? t('da scaduto, ogni {n} giorni', { n: r.repeat_expired_days })
      : t('da scaduto, una volta'))
  }
  return parti.join(' · ')
}

function qualiCertificati(r: CertAlertRuleIn): string {
  const parti = [r.only_exposed ? t('raggiungibili da altre macchine') : t('anche quelli solo in locale')]
  if (r.match.length) parti.push(t('che contengono {parole}', { parole: r.match.join(', ') }))
  if (r.services.length) parti.push(t('dei servizi {servizi}', { servizi: r.services.join(', ') }))
  if (r.kind === 'scadenza' && r.repository_types.length) {
    parti.push(t('e del repository non usati ({tipi})', {
      tipi: r.repository_types.map((x) => TIPI_REPOSITORY.find(([k]) => k === x)?.[1]() ?? x).join(', '),
    }))
  }
  if (r.min_criticality) {
    parti.push(t('criticità almeno {c}', { c: (CRITICALITY_LABEL[r.min_criticality] ?? r.min_criticality).toLowerCase() }))
  }
  return parti.join(' · ')
}

function chiLeRiceve(r: CertAlertRuleIn): string {
  const parti = [...r.recipients.slice(0, 3)]
  if (r.recipients.length > 3) parti.push(t('altri {n}', { n: r.recipients.length - 3 }))
  if (r.include_tenant_recipients) parti.push(t('i destinatari generali'))
  if (r.include_service_owners) parti.push(t('i responsabili'))
  return parti.join(', ')
}

export function motivoAvviso(h: Pick<CertAlertSentInfo, 'threshold' | 'kind'>): string {
  if (h.kind === 'autofirmato' || h.threshold === null) return t('autofirmato')
  if (h.threshold === 0) return t('scaduto')
  return t('{n} giorni prima', { n: h.threshold })
}

export type VistaNotifiche = 'regole' | 'log'

export function NotificheCertificati({ dati, vista, onVista }: {
  dati: TlsRegister
  vista: VistaNotifiche
  onVista: (v: VistaNotifiche) => void
}) {
  const panoramica = useAsync(() => api.certAlerts(), [])
  const scrive = usePermessiModuli().scrive('plaintext_traffic')
  const [nuova, setNuova] = useState<CertAlertRuleIn | null>(null)
  const [inModifica, setInModifica] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)

  const p = panoramica.data
  const s = p?.summary

  function crea(kind: CertAlertRule['kind']) {
    setInModifica(null)
    setNuova({ ...NUOVA[kind], name: kind === 'scadenza' ? t('Scadenze dei certificati') : t('Certificati autofirmati') })
    onVista('regole')
  }

  async function alterna(r: CertAlertRule) {
    setErrore(null)
    try {
      await api.updateCertAlertRule(r.id, { enabled: !r.enabled })
      await panoramica.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  async function elimina(r: CertAlertRule) {
    if (!window.confirm(t('Eliminare la regola «{nome}»? Lo storico dei suoi avvisi si perde con lei.', { nome: r.name }))) return
    setErrore(null)
    try {
      await api.deleteCertAlertRule(r.id)
      await panoramica.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  const colonne: Column<CertAlertRule>[] = [
    { key: 'name', header: t('Regola'), value: (r) => r.name, render: (r) => (
      <>
        <strong>{r.name}</strong><br />
        <span className={`badge ${r.kind === 'scadenza' ? 'badge--info' : 'badge--warn'}`}>
          {r.kind === 'scadenza' ? t('Scadenze') : t('Autofirmati')}
        </span>
        {!r.enabled && <> <span className="badge badge--muted">{t('spenta')}</span></>}
      </>
    ) },
    { key: 'quando', header: t('Quando avvisa'), value: (r) => quandoAvvisa(r) },
    { key: 'quali', header: t('Quali certificati'), value: (r) => qualiCertificati(r) },
    { key: 'chi', header: t('Chi li riceve'), value: (r) => chiLeRiceve(r) },
    { key: 'last_sent_at', header: t('Ultimo avviso'), type: 'date', value: (r) => r.last_sent_at ?? '', render: (r) => (
      <>
        <small>{r.last_sent_at ? formatDateTime(r.last_sent_at) : t('mai')}</small>
        {r.last_error && <><br /><small style={{ color: 'var(--error)' }}>{erroreRegola(r.last_error)}</small></>}
      </>
    ) },
    { key: 'azioni', header: '', filter: false, sort: false, nowrap: true, render: (r) => (
      <div className="toolbar" style={{ margin: 0, gap: 6, flexWrap: 'nowrap' }}>
        <button type="button" className="secondary" onClick={() => { setNuova(null); setInModifica(inModifica === r.id ? null : r.id) }}>
          {inModifica === r.id ? t('Chiudi') : scrive ? t('Modifica') : t('Apri')}
        </button>
        {scrive && (
          <button type="button" className="secondary" onClick={() => void alterna(r)}>
            {r.enabled ? t('Spegni') : t('Accendi')}
          </button>
        )}
      </div>
    ) },
  ]

  if (panoramica.loading && !p) return <Spinner />
  return (
    <>
      <ErrorBanner error={panoramica.error ?? errore} />
      {p && s && (
        <>
          <div className="stats">
            <StatCard label={t('Regole attive')} value={s.enabled} tone={s.enabled ? 'ok' : 'warn'}
              hint={s.rules === 0 ? t('Nessuna regola: nessuno viene avvisato quando un certificato sta per scadere.') : undefined}
              onClick={() => onVista('regole')} />
            <StatCard label={t('Regole con un problema')} value={s.with_errors} tone={s.with_errors ? 'error' : 'ok'}
              onClick={() => onVista('regole')} />
            <StatCard label={t('Certificati segnalati negli ultimi 30 giorni')} value={s.sent_30d} onClick={() => onVista('log')} />
            <StatCard label={t('Ultimo avviso')}
              value={s.last_sent_at ? new Date(s.last_sent_at).toLocaleDateString(localeCorrente()) : '—'}
              onClick={() => onVista('log')} />
          </div>
          <div className="toolbar" style={{ flexWrap: 'wrap', gap: 6, marginBottom: 8 }} role="tablist">
            <button type="button" role="tab" aria-selected={vista === 'regole'} className={vista === 'regole' ? '' : 'secondary'}
              onClick={() => onVista('regole')}>
              {t('Regole')} ({s.rules})
            </button>
            <button type="button" role="tab" aria-selected={vista === 'log'} className={vista === 'log' ? '' : 'secondary'}
              onClick={() => onVista('log')}>
              {t('Log notifiche')} ({p.history.length})
            </button>
          </div>

          {vista === 'regole' ? (
            <Card title={t('Regole di notifica')}>
              <p className="field__hint" style={{ marginTop: 0 }}>
                {t('Le regole guardano il registro una volta l’ora. Ogni soglia parte una volta sola per certificato (la più vicina, se ne sono passate più d’una insieme) e un certificato rinnovato ricomincia da capo. Ogni destinatario riceve una sola mail con tutti i suoi certificati di quel giro.')}
              </p>
              {scrive && (
                <div className="toolbar">
                  <button type="button" onClick={() => crea('scadenza')}>{t('Nuova regola: scadenze')}</button>
                  <button type="button" className="secondary" onClick={() => crea('autofirmato')}>{t('Nuova regola: autofirmati')}</button>
                </div>
              )}
              {nuova && (
                <div className="card" style={{ padding: 16, marginBottom: 16 }}>
                  <EditorRegola iniziale={nuova} dati={dati} utenti={p.users} generali={p.general_recipients}
                    responsabili={p.service_owners} scrive={scrive}
                    onAnnulla={() => setNuova(null)}
                    onSalvata={async () => { setNuova(null); await panoramica.reload() }} />
                </div>
              )}
              {p.rules.length === 0 && !nuova ? (
                <EmptyState title={t('Nessuna regola: nessuno viene avvisato quando un certificato sta per scadere.')}
                  hint={scrive ? t('Crea una regola sulle scadenze o sugli autofirmati e scegli chi riceve gli avvisi.') : undefined} />
              ) : p.rules.length > 0 && (
                <DataTable rows={p.rules} rowKey={(r) => r.id} columns={colonne} conteggio={{ nome: t('regole') }}
                  expanded={(r) => r.id === inModifica}
                  expand={(r) => (
                    <div style={{ padding: '8px 4px' }}>
                      <EditorRegola iniziale={r} regola={r} dati={dati} utenti={p.users} generali={p.general_recipients}
                        responsabili={p.service_owners} scrive={scrive}
                        onAnnulla={() => setInModifica(null)}
                        onSalvata={async () => { setInModifica(null); await panoramica.reload() }}
                        onElimina={scrive ? () => void elimina(r) : undefined} />
                    </div>
                  )} />
              )}
            </Card>
          ) : (
            <Card title={t('Log notifiche')}>
              <p className="field__hint" style={{ marginTop: 0 }}>
                {t('Ogni certificato di cui una regola ha avvisato qualcuno: quando, perché e chi ha ricevuto la mail. Gli avvisi non consegnati a nessuno non compaiono: la regola li riprova al giro dopo.')}
              </p>
              <StoricoAvvisi righe={p.history} />
            </Card>
          )}
        </>
      )}
    </>
  )
}

function StoricoAvvisi({ righe }: { righe: CertAlertSentInfo[] }) {
  const colonne: Column<CertAlertSentInfo>[] = [
    { key: 'sent_at', header: t('Quando'), type: 'date', value: (h) => h.sent_at, render: (h) => <small>{formatDateTime(h.sent_at)}</small> },
    { key: 'rule_name', header: t('Regola'), value: (h) => h.rule_name },
    { key: 'cn', header: t('Certificato'), value: (h) => `${h.cn} ${h.ci_name}`, render: (h) => (
      <>
        <strong>{h.cn || '—'}</strong><br />
        <small className="text-muted">
          {nonUsato(h.cert_key)
            ? <Link to="/certificates?tab=repository">{t('non usato · repository')}</Link>
            : <>{h.ci_id ? <Link to={`/cis/${h.ci_id}`}>{h.ci_name}</Link> : h.ci_name}<span className="mono">:{h.port}</span></>}
        </small>
      </>
    ) },
    { key: 'motivo', header: t('Perché'), value: (h) => motivoAvviso(h) },
    { key: 'recipients', header: t('Chi l’ha ricevuto'), value: (h) => h.recipients.join(', '), render: (h) => <small>{h.recipients.join(', ')}</small> },
  ]
  return (
    <DataTable rows={righe} rowKey={(h) => h.id} columns={colonne} initialSort={{ key: 'sent_at', dir: 'desc' }}
      conteggio={{ nome: t('avvisi inviati') }} emptyText={t('Nessun avviso inviato finora.')} />
  )
}

function EditorRegola({ iniziale, regola, dati, utenti, generali, responsabili, scrive, onSalvata, onAnnulla, onElimina }: {
  iniziale: CertAlertRuleIn
  regola?: CertAlertRule
  dati: TlsRegister
  utenti: string[]
  generali: string[]
  responsabili: Array<{ service: string; owner: string }>
  scrive: boolean
  onSalvata: () => Promise<void>
  onAnnulla: () => void
  onElimina?: () => void
}) {
  const { principal } = useAuth()
  const [r, setR] = useState<CertAlertRuleIn>(() => ({
    name: iniziale.name, kind: iniziale.kind, enabled: iniziale.enabled, days: [...iniziale.days],
    include_expired: iniziale.include_expired, repeat_expired_days: iniziale.repeat_expired_days,
    notify_existing: iniziale.notify_existing, only_exposed: iniziale.only_exposed, match: [...iniziale.match],
    services: [...iniziale.services], repository_types: [...iniziale.repository_types],
    min_criticality: iniziale.min_criticality, recipients: [...iniziale.recipients],
    include_tenant_recipients: iniziale.include_tenant_recipients, include_service_owners: iniziale.include_service_owners,
  }))
  const [parole, setParole] = useState(iniziale.match.join(', '))
  const [nuovoGiorno, setNuovoGiorno] = useState('')
  const [nuovoDestinatario, setNuovoDestinatario] = useState('')
  const [salvo, setSalvo] = useState(false)
  const [errore, setErrore] = useState<unknown>(null)
  const [anteprima, setAnteprima] = useState<CertAlertPreview | null>(null)
  const [erroreAnteprima, setErroreAnteprima] = useState<string | null>(null)
  const [prova, setProva] = useState<string | null>(null)
  const bloccato = !scrive || salvo
  const cambia = (parziale: Partial<CertAlertRuleIn>) => { setR((x) => ({ ...x, ...parziale })); setProva(null) }

  // The business services the register's certificates stand on: the choices.
  const servizi = useMemo(() => [...new Set(dati.items.flatMap((c) => c.services))].sort(), [dati])
  const id = regola?.id ?? 'nuova'

  // Live preview of the next pass, a moment after the last change.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      api.previewCertAlertRule({ ...r, rule_id: regola?.id })
        .then((a) => { setAnteprima(a); setErroreAnteprima(null) })
        .catch((e) => { setAnteprima(null); setErroreAnteprima(messaggio(e)) })
    }, 500)
    return () => window.clearTimeout(timer)
  }, [r, regola?.id])

  function alternaGiorno(g: number) {
    cambia({ days: (r.days.includes(g) ? r.days.filter((x) => x !== g) : [...r.days, g]).sort((a, b) => b - a) })
  }

  function aggiungiGiorno() {
    const g = Number(nuovoGiorno)
    if (Number.isInteger(g) && g >= 1 && g <= 365 && !r.days.includes(g)) alternaGiorno(g)
    setNuovoGiorno('')
  }

  function aggiungiDestinatari(testo: string) {
    const nuovi = testo.split(/[\s,;]+/).map((x) => x.trim().toLowerCase()).filter((x) => x && !r.recipients.includes(x))
    if (nuovi.length) cambia({ recipients: [...r.recipients, ...nuovi] })
    setNuovoDestinatario('')
  }

  function corpo(): CertAlertRuleIn {
    return { ...r, match: parole.split(',').map((x) => x.trim()).filter(Boolean) }
  }

  async function salva() {
    setSalvo(true)
    setErrore(null)
    try {
      if (regola) await api.updateCertAlertRule(regola.id, corpo())
      else await api.createCertAlertRule(corpo())
      await onSalvata()
    } catch (e) {
      setErrore(e)
    } finally {
      setSalvo(false)
    }
  }

  async function mandaProva() {
    setErrore(null)
    setProva(null)
    try {
      const out = await api.testCertAlertRule({ ...corpo(), rule_id: regola?.id })
      setProva(t('Prova mandata a {email} con {n} certificati.', { email: out.sent_to, n: out.certificates }))
    } catch (e) {
      setErrore(e)
    }
  }

  const responsabiliConEmail = responsabili.length

  return (
    <div>
      <div className="toolbar" style={{ alignItems: 'flex-end' }}>
        <div className="field" style={{ flex: 1, minWidth: 240 }}>
          <label htmlFor={`regola-nome-${id}`}>{t('Nome della regola')}</label>
          <input id={`regola-nome-${id}`} type="text" maxLength={120} value={r.name} disabled={bloccato}
            onChange={(e) => cambia({ name: e.target.value })} />
        </div>
        <div className="field">
          <label>{t('Avvisa per')}</label>
          <div className="toolbar" style={{ margin: 0, gap: 6 }}>
            {(['scadenza', 'autofirmato'] as const).map((k) => (
              <button key={k} type="button" className={r.kind === k ? '' : 'secondary'} disabled={bloccato}
                aria-pressed={r.kind === k}
                onClick={() => cambia(k === r.kind ? {} : { ...NUOVA[k], name: r.name, recipients: r.recipients,
                  include_tenant_recipients: r.include_tenant_recipients, include_service_owners: r.include_service_owners,
                  only_exposed: r.only_exposed, services: r.services, min_criticality: r.min_criticality })}>
                {k === 'scadenza' ? t('Scadenze') : t('Autofirmati')}
              </button>
            ))}
          </div>
        </div>
      </div>

      <h4 style={{ margin: '12px 0 6px' }}>{t('Quando')}</h4>
      {r.kind === 'scadenza' ? (
        <>
          <div className="field">
            <label>{t('Giorni prima della scadenza')}</label>
            <div className="toolbar" style={{ margin: 0, gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
              {[...new Set([...GIORNI_TIPICI, ...r.days])].sort((a, b) => b - a).map((g) => (
                <button key={g} type="button" className={r.days.includes(g) ? '' : 'secondary'} disabled={bloccato}
                  aria-pressed={r.days.includes(g)} onClick={() => alternaGiorno(g)}>
                  {g}
                </button>
              ))}
              <input type="number" min={1} max={365} style={{ width: 90 }} value={nuovoGiorno} disabled={bloccato}
                placeholder={t('altro')} aria-label={t('Altro numero di giorni')}
                onChange={(e) => setNuovoGiorno(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aggiungiGiorno() } }} />
              <button type="button" className="secondary" disabled={bloccato || !nuovoGiorno} onClick={aggiungiGiorno}>{t('Aggiungi')}</button>
            </div>
            <div className="field__hint">{t('Una mail a ogni soglia. Se il certificato è già oltre più soglie quando la regola nasce, parte una mail sola, per la più vicina.')}</div>
          </div>
          <div className="checkbox">
            <input id={`regola-scaduti-${id}`} type="checkbox" checked={r.include_expired} disabled={bloccato}
              onChange={(e) => cambia({ include_expired: e.target.checked })} />
            <label htmlFor={`regola-scaduti-${id}`}>
              {t('Avvisa anche quando è scaduto ed è ancora installato')}
              <small>{t('Un certificato scaduto che nessuno sostituisce: il caso peggiore.')}</small>
            </label>
          </div>
          {r.include_expired && (
            <div className="field" style={{ maxWidth: 320 }}>
              <label htmlFor={`regola-ripeti-${id}`}>{t('Ripeti l’avviso da scaduto ogni (giorni)')}</label>
              <input id={`regola-ripeti-${id}`} type="number" min={0} max={90} value={r.repeat_expired_days} disabled={bloccato}
                onChange={(e) => cambia({ repeat_expired_days: Math.max(0, Math.min(90, Number(e.target.value) || 0)) })} />
              <div className="field__hint">{t('0 = una volta sola.')}</div>
            </div>
          )}
        </>
      ) : (
        <div className="checkbox">
          <input id={`regola-esistenti-${id}`} type="checkbox" checked={r.notify_existing} disabled={bloccato || Boolean(regola)}
            onChange={(e) => cambia({ notify_existing: e.target.checked })} />
          <label htmlFor={`regola-esistenti-${id}`}>
            {t('Al primo controllo manda anche quelli già presenti, in un riepilogo')}
            <small>
              {regola ? t('Vale solo alla nascita della regola.')
                : t('Spenta: si avvisa solo per quelli che compaiono da adesso; gli attuali sono nella scheda «Certificati autofirmati».')}
            </small>
          </label>
        </div>
      )}

      <h4 style={{ margin: '12px 0 6px' }}>{t('Quali certificati')}</h4>
      <div className="checkbox">
        <input id={`regola-esposti-${id}`} type="checkbox" checked={r.only_exposed} disabled={bloccato}
          onChange={(e) => cambia({ only_exposed: e.target.checked })} />
        <label htmlFor={`regola-esposti-${id}`}>
          {t('Solo quelli raggiungibili da altre macchine')}
          <small>{t('Lascia fuori i servizi in ascolto solo sulla macchina stessa (127.0.0.1).')}</small>
        </label>
      </div>
      <div className="toolbar" style={{ alignItems: 'flex-start' }}>
        <div className="field" style={{ flex: 1, minWidth: 240 }}>
          <label htmlFor={`regola-parole-${id}`}>{t('Nome, SAN, emittente o CI contengono')}</label>
          <input id={`regola-parole-${id}`} type="text" value={parole} disabled={bloccato}
            placeholder={t('vuoto: tutti')} onChange={(e) => { setParole(e.target.value); cambia({ match: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) }) }} />
          <div className="field__hint">{t('Più parole separate da virgola: basta una (ospedale.it, pacs).')}</div>
        </div>
        <div className="field" style={{ minWidth: 200 }}>
          <label htmlFor={`regola-criticita-${id}`}>{t('Criticità del servizio almeno')}</label>
          <select id={`regola-criticita-${id}`} value={r.min_criticality} disabled={bloccato}
            onChange={(e) => cambia({ min_criticality: e.target.value })}>
            <option value="">{t('qualsiasi')}</option>
            {(['critical', 'high', 'medium', 'low'] as const).map((c) => (
              <option key={c} value={c}>{CRITICALITY_LABEL[c] ?? c}</option>
            ))}
          </select>
        </div>
      </div>
      {servizi.length > 0 && (
        <div className="field">
          <label>{t('Solo i certificati di questi business service')}</label>
          <div className="toolbar" style={{ margin: 0, gap: 6, flexWrap: 'wrap' }}>
            {servizi.map((sv) => (
              <button key={sv} type="button" className={r.services.includes(sv) ? '' : 'secondary'} disabled={bloccato}
                aria-pressed={r.services.includes(sv)}
                onClick={() => cambia({ services: r.services.includes(sv) ? r.services.filter((x) => x !== sv) : [...r.services, sv] })}>
                {sv}
              </button>
            ))}
          </div>
          <div className="field__hint">{t('Nessuno scelto: tutti.')}</div>
        </div>
      )}

      {r.kind === 'scadenza' && (
        <div className="field">
          <label>{t('Anche i certificati del repository che nessuna macchina usa')}</label>
          <div className="toolbar" style={{ margin: 0, gap: 6, flexWrap: 'wrap' }}>
            {TIPI_REPOSITORY.map(([k, etichetta]) => (
              <button key={k} type="button" className={r.repository_types.includes(k) ? '' : 'secondary'} disabled={bloccato}
                aria-pressed={r.repository_types.includes(k)}
                onClick={() => cambia({ repository_types: r.repository_types.includes(k)
                  ? r.repository_types.filter((x) => x !== k) : [...r.repository_types, k] })}>
                {etichetta()}
              </button>
            ))}
          </div>
          <div className="field__hint">
            {t('I certificati di Gestione certificati che nessuna macchina espone: nella mail sono segnati «non usato». Quelli già sostituiti da uno più recente con lo stesso nome non si segnalano; computer e utenti si rinnovano da soli con l’autoenrollment e sono spenti di default. Nessun tipo scelto: il repository resta fuori.')}
          </div>
        </div>
      )}

      <h4 style={{ margin: '12px 0 6px' }}>{t('Chi le riceve')}</h4>
      <div className="field">
        <label htmlFor={`regola-destinatari-${id}`}>{t('Indirizzi email')}</label>
        <div className="toolbar" style={{ margin: 0, gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {r.recipients.map((e) => (
            <span key={e} className="badge badge--info" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {e}
              {!bloccato && (
                <button type="button" className="link" aria-label={t('Togli {email}', { email: e })}
                  onClick={() => cambia({ recipients: r.recipients.filter((x) => x !== e) })}>×</button>
              )}
            </span>
          ))}
          <input id={`regola-destinatari-${id}`} type="email" list={`regola-utenti-${id}`} style={{ width: 'auto', flex: '1 1 240px', maxWidth: 420 }}
            value={nuovoDestinatario} disabled={bloccato} placeholder={t('nome@azienda.it')}
            onChange={(e) => setNuovoDestinatario(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); aggiungiDestinatari(nuovoDestinatario) } }} />
          <datalist id={`regola-utenti-${id}`}>
            {utenti.filter((u) => !r.recipients.includes(u)).map((u) => <option key={u} value={u} />)}
          </datalist>
          <button type="button" className="secondary" disabled={bloccato || !nuovoDestinatario.trim()}
            onClick={() => aggiungiDestinatari(nuovoDestinatario)}>{t('Aggiungi')}</button>
        </div>
        <div className="field__hint">{t('Utenti del tenant o qualunque indirizzo (una lista di gruppo, il fornitore che gestisce il servizio).')}</div>
      </div>
      <div className="checkbox">
        <input id={`regola-generali-${id}`} type="checkbox" checked={r.include_tenant_recipients} disabled={bloccato}
          onChange={(e) => cambia({ include_tenant_recipients: e.target.checked })} />
        <label htmlFor={`regola-generali-${id}`}>
          {t('Anche i destinatari generali delle notifiche')}
          <small>
            {generali.length ? generali.join(', ')
              : t('Nessuno impostato: si impostano in Impostazioni → Posta in uscita.')}
          </small>
        </label>
      </div>
      <div className="checkbox">
        <input id={`regola-responsabili-${id}`} type="checkbox" checked={r.include_service_owners} disabled={bloccato}
          onChange={(e) => cambia({ include_service_owners: e.target.checked })} />
        <label htmlFor={`regola-responsabili-${id}`}>
          {t('Anche i responsabili, ciascuno solo per i suoi certificati')}
          <small>
            {t('Il responsabile del business service e, per i certificati del repository, quello scritto sul certificato, quando è un indirizzo email.')}{' '}
            {responsabiliConEmail
              ? t('Servizi con un indirizzo email come responsabile: {elenco}', {
                elenco: responsabili.slice(0, 4).map((x) => `${x.service} → ${x.owner}`).join(', ') + (responsabiliConEmail > 4 ? '…' : ''),
              })
              : t('Nessun business service ha come responsabile un indirizzo email: si imposta nella scheda del servizio.')}
          </small>
        </label>
      </div>

      <Anteprima a={anteprima} errore={erroreAnteprima} kind={r.kind} />

      <ErrorBanner error={errore} />
      <div className="toolbar" style={{ alignItems: 'center', marginBottom: 0 }}>
        {scrive ? (
          <>
            <button type="button" onClick={() => void salva()} disabled={salvo || !r.name.trim()}>
              {salvo ? t('Salvataggio…') : regola ? t('Salva') : t('Crea la regola')}
            </button>
            <button type="button" className="secondary" onClick={onAnnulla} disabled={salvo}>{t('Annulla')}</button>
            <button type="button" className="secondary" onClick={() => void mandaProva()} disabled={salvo || !principal?.email}
              title={principal?.email ? undefined : t('Il tuo profilo non ha un indirizzo email')}>
              {t('Manda una prova a me')}
            </button>
            {onElimina && <button type="button" className="danger" onClick={onElimina} disabled={salvo}>{t('Elimina')}</button>}
            {prova && <span className="badge badge--ok">{prova}</span>}
          </>
        ) : (
          <span className="text-muted">{t('Hai la sola lettura della Cifratura: le regole non si possono cambiare.')}</span>
        )}
      </div>
      {regola && (
        <p className="field__hint" style={{ marginBottom: 0 }}>
          {t('Creata da {chi}; ultima modifica di {modifica}.', { chi: regola.created_by || '—', modifica: regola.updated_by || '—' })}
        </p>
      )}
    </div>
  )
}

function Anteprima({ a, errore, kind }: { a: CertAlertPreview | null; errore: string | null; kind: CertAlertRule['kind'] }) {
  if (errore) {
    return <p className="field__hint" style={{ color: 'var(--warn)' }}>{t('Anteprima: {errore}', { errore })}</p>
  }
  if (!a) return null
  return (
    <div style={{ background: 'var(--surface-sunken)', borderRadius: 8, padding: '10px 12px', margin: '14px 0' }}>
      <strong>{t('Al prossimo controllo')}</strong>
      <p style={{ margin: '4px 0' }}>
        {a.baseline > 0
          ? t('{n} autofirmati già presenti vengono annotati senza mandare niente; da lì in poi si avvisa per i nuovi.', { n: a.baseline })
          : a.due.length === 0
            ? t('Niente da mandare adesso: la regola copre {n} certificati del registro e nessuno ha passato una soglia.', { n: a.matching })
            : t('Partirebbero {n} certificati, a {m} destinatari.', { n: a.due.length, m: a.recipients.length })}
      </p>
      {a.due.length > 0 && (
        <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
          {a.due.slice(0, 8).map((d) => (
            <li key={d.key}>
              <strong>{d.cn || '—'}</strong>{' '}
              {d.unused ? <span className="badge badge--muted">{t('non usato')}</span>
                : <small className="text-muted">{d.ci_name}:{d.port}</small>}
              {' — '}{kind === 'autofirmato' ? t('autofirmato') : motivoAvviso({ threshold: d.threshold, kind })}
            </li>
          ))}
          {a.due.length > 8 && <li className="text-muted">{t('e altri {n}', { n: a.due.length - 8 })}</li>}
        </ul>
      )}
      {a.recipients.length > 0 && (
        <small className="text-muted">
          {t('Riceverebbero: {elenco}', { elenco: a.recipients.map((x) => `${x.email} (${x.certificates})`).join(', ') })}
        </small>
      )}
      {a.recipients.length === 0 && (
        <small style={{ color: 'var(--error)' }}>{t('Nessuno riceverebbe questi avvisi: aggiungi un destinatario.')}</small>
      )}
    </div>
  )
}

/** In the detail of one certificate: who was told about it, and when. */
export function AvvisiDelCertificato({ chiave }: { chiave: string }) {
  const storia = useAsync(() => api.certAlertHistory(chiave), [chiave])
  if (storia.error) return <small className="text-muted">—</small>
  const righe = storia.data ?? []
  if (!storia.data) return <small className="text-muted">…</small>
  if (righe.length === 0) return <small className="text-muted">{t('Nessun avviso inviato per questo certificato.')}</small>
  return (
    <ul style={{ margin: 0, paddingLeft: 18 }}>
      {righe.slice(0, 6).map((h) => (
        <li key={h.id}>
          <small>
            {formatDateTime(h.sent_at)} · {motivoAvviso(h)} · {h.rule_name} → {h.recipients.join(', ')}
          </small>
        </li>
      ))}
    </ul>
  )
}
